import React from "react";
import styled from "@emotion/styled";
import Stack from "../mui/Stack";
import Typography from "../mui/Typography";
import Checkbox from "../mui/Checkbox";
import FormControlLabel from "../mui/FormControlLabel";
import AutoComplete from "../mui/AutoComplete";

interface SelectOption {
  label: string;
  id?: string | number | boolean;
}
export type FilterStateType = {
  regions: RegionType[];
  selectedRegion: RegionType[];
  center: CenterType | null;
  programPerformanceParameter: ProgramParameterType | null;
  qualityEfficacyParameter: QualityParameterType | null;
  state: StateType | null;
  cityManager: CityManagerType | null;
  funder: FunderType | null;
  regionalDataManager: RegionalDataManagerType | null;
  clusterManager: ClusterManagerType | null;
  medals: MedalType | null;
  city: CityType | null;
  programMasterId: string | null;
  ProgramYearId: string | null;
  quarterMasterId: string | null;
};

export type RegionType = { id: string; label: string };
export type CenterType = {
  id?: string;
  centerSelect: SelectOption[];
  centerTypeSelect: SelectOption[];
  citySelect: SelectOption[];
  districtSelect: SelectOption[];
  stateSelect: SelectOption[];
};
export type ProgramParameterType = { id: number; label: string };
export type QualityParameterType = { id: number; label: string };
export type StateType = { id: string; label: string };
export type CityType = { id: string; label: string };
export type CityManagerType = { id: string | number; label: string };
export type ClusterManagerType = { id: string | number; label: string };
export type FunderType = { id: number; label: string };
export type RegionalDataManagerType = { id: string | number; label: string };
export type MedalType = { id: string; label: string };

interface ReportFilterFormProps {
  selectedTab: { id: number; name: string; code: string } | null;
  regions: RegionType[];
  centerList: CenterType;
  programParameters: ProgramParameterType[];
  qualityParameters: QualityParameterType[];
  city: CityType[];
  states: StateType[];
  cityManagers: CityManagerType[];
  clusterManagers: ClusterManagerType[];
  funders: FunderType[];
  regionalDataManagers: RegionalDataManagerType[];
  medals: MedalType[];
  filter: FilterStateType;
  handleFilterFormChange: (field: keyof FilterStateType, value: any) => void;
}

const ReportFilterForm: React.FC<ReportFilterFormProps> = ({
  selectedTab,
  programParameters,
  qualityParameters,
  regions,
  centerList,
  states,
  city,
  cityManagers,
  clusterManagers,
  funders,
  regionalDataManagers,
  medals,
  filter,
  handleFilterFormChange,
}) => {
  return (
    <Stack>
      {/* Region */}
      <StyledStack>
        <Typography>Region</Typography>
        <Stack direction="row" spacing={1}>
          {regions.map((region) => (
            <FormControlLabel
              key={region.id}
              control={
                <Checkbox
                  sx={{
                    fontSize: "10px",
                    "& .MuiSvgIcon-root": {
                      color: "#828282 !important",
                    },
                    "&.Mui-checked .MuiSvgIcon-root": {
                      color: "#FFCA09 !important",
                    },
                  }}
                  checked={filter.selectedRegion.some(
                    (r) => r.id === region.id
                  )}
                  onChange={() => {
                    const updatedRegions = filter.selectedRegion.some(
                      (r) => r.id === region.id
                    )
                      ? filter.selectedRegion.filter((r) => r.id !== region.id)
                      : [...filter.selectedRegion, region];
                    handleFilterFormChange("selectedRegion", updatedRegions);
                  }}
                />
              }
              label={region.label}
            />
          ))}
        </Stack>
      </StyledStack>

      {/* Medals */}
      <StyledStack>
        <Typography>Medals</Typography>
        <AutoComplete
          sx={{ background: "#fff" }}
          options={medals}
          labelKey="label"
          matchKey="id"
          onChange={(e) => handleFilterFormChange("medals", e)}
          value={filter.medals}
          placeholder="Select Medals"
        />
      </StyledStack>

      {/* State */}
      <StyledStack>
        <Typography>State</Typography>
        <AutoComplete
          sx={{ background: "#fff" }}
          options={states}
          labelKey="label"
          matchKey="id"
          onChange={(e) => handleFilterFormChange("state", e)}
          value={filter.state}
          placeholder="Select State"
          disableClearable={false}
        />
      </StyledStack>
      <StyledStack>
        <Typography>City</Typography>
        <AutoComplete
          sx={{ background: "#fff" }}
          options={city ?? []}
          labelKey="label"
          matchKey="id"
          onChange={(e) => handleFilterFormChange("city", e)}
          value={filter.city}
          placeholder="Select City"
          disableClearable={false}
        />
      </StyledStack>

      {/* Center */}
      <StyledStack>
        <Typography>Centre</Typography>
        <AutoComplete
          sx={{ background: "#fff" }}
          options={centerList.centerSelect}
          labelKey="label"
          matchKey="id"
          onChange={(e) => handleFilterFormChange("center", e)}
          value={filter.center}
          placeholder="Select Centre"
        />
      </StyledStack>

      {/* City Manager */}
      <StyledStack>
        <Typography>City Manager</Typography>
        <AutoComplete
          sx={{ background: "#fff" }}
          options={cityManagers}
          labelKey="label"
          matchKey="id"
          onChange={(e) => handleFilterFormChange("cityManager", e)}
          value={filter.cityManager}
          placeholder="Select City Manager"
        />
      </StyledStack>

      {/* Funder */}
      <StyledStack>
        <Typography>Funder</Typography>
        <AutoComplete
          sx={{ background: "#fff" }}
          options={funders}
          labelKey="label"
          matchKey="id"
          onChange={(e) => handleFilterFormChange("funder", e)}
          value={filter.funder}
          placeholder="Select Funder"
        />
      </StyledStack>

      {/* Regional Data Manager */}
      <StyledStack>
        <Typography>Regional Data Manager</Typography>
        <AutoComplete
          sx={{ background: "#fff" }}
          options={regionalDataManagers}
          labelKey="label"
          matchKey="id"
          onChange={(e) => handleFilterFormChange("regionalDataManager", e)}
          value={filter.regionalDataManager}
          placeholder="Select Regional Data Manager"
        />
      </StyledStack>

      {/* Cluster Manager */}
      <StyledStack>
        <Typography>Cluster Manager</Typography>
        <AutoComplete
          sx={{ background: "#fff" }}
          options={clusterManagers}
          labelKey="label"
          matchKey="id"
          onChange={(e) => handleFilterFormChange("clusterManager", e)}
          value={filter.clusterManager}
          placeholder="Select Cluster Manager"
        />
      </StyledStack>
    </Stack>
  );
};

const StyledStack = styled(Stack)(() => ({
  marginBottom: "10px",
}));

export default ReportFilterForm;
